import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { createContext, type ReactNode, useContext } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { type DispatchTableRow, rowTask } from "@/hooks/dispatch/dispatchTableRow";
import type { TaskMenuAction } from "@/hooks/dispatch/taskMenu";
import type { ItemTarget, TaskFold } from "@/hooks/dispatch/tasks";
import { zinc } from "@/ui/tokens";
import { DISPATCH_TASK } from "./dispatchColors";
import { formatRelativeTime } from "./format";

/**
 * What the page's task surfaces read while the workspace shows tasks: the fold, a board's label, a member's name, and
 * the minute clock. Null while tasks are off, and every task surface then renders nothing.
 */
export type DispatchTaskState = {
  fold: TaskFold;
  /** A board's label by view id; null for a view the workspace no longer has. */
  viewLabelFor: (viewId: string) => string | null;
  /** A member's name by membership id; null when the store has none. */
  memberNameFor: (membershipId: string | null) => string | null;
  now: number;
  /** The driver the History panel is open on, whom a task from one of its entries can name instead; null for none. */
  historyDriverId: string | null;
  /** Whether a new task may be about a message or event; off, every new task is about the driver. */
  itemTasks: boolean;
  /**
   * Runs a task menu entry from History: make opens the picker on the message or event, or on the panel's driver when
   * `target` is null; move opens the picker on the entry's task in Move mode.
   */
  onItemTaskAction: (target: ItemTarget | null, action: TaskMenuAction) => void;
};

/** The page's task state for the cells and History; null while the workspace shows no tasks. */
export const DispatchTaskContext = createContext<DispatchTaskState | null>(null);

/** What a chip names when the task is on no board the workspace has: it shows on every shelf. */
const EVERY_BOARD = "Every board";

/**
 * The purple chip naming the board a row's open task is pinned to, with its note and its author on hover.
 *
 * Renders nothing for a row with no open task, or while tasks are off. The red dot keeps its meaning beside it.
 */
export function TaskChip({ row }: { row: DispatchTableRow }) {
  const state = useContext(DispatchTaskContext);
  const open = state ? rowTask(row, state.fold) : null;
  if (!state || !open) return null;
  const board = open.pin ? (state.viewLabelFor(open.pin.viewId) ?? EVERY_BOARD) : EVERY_BOARD;
  const author = state.memberNameFor(open.task.createdBy);
  const made = formatRelativeTime(open.task.createdAt, state.now)?.short ?? "";
  const byline = author ? `${author}, ${made}` : made;
  return (
    <Tooltip title={open.task.note ? `${open.task.note} (${byline})` : byline}>
      <Box
        component="span"
        data-sb={TestIds.dispatch.taskChip}
        sx={{
          flex: "none",
          px: 0.75,
          height: 18,
          lineHeight: "18px",
          borderRadius: 9,
          border: `1px solid ${DISPATCH_TASK}`,
          color: DISPATCH_TASK,
          fontSize: "0.6875rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {board}
      </Box>
    </Tooltip>
  );
}

/**
 * What a Message cell shows in place of the message while the row's open task carries a note: the assigner's name and
 * the note, or the note alone when the store has no name for the assigner. Null for a task with no note.
 *
 * @param open - The row's open task.
 * @param memberNameFor - A member's name by membership id, as the task's author cell reads it.
 */
export function taskNoteLine(open: OpenTask, memberNameFor: DispatchTaskState["memberNameFor"]): string | null {
  const note = open.task.note?.trim();
  if (!note) return null;
  const assigner = memberNameFor(open.task.createdBy);
  return assigner ? `${assigner}: ${note}` : note;
}

/** A task's note line behind a note icon, with the message it stands in for on hover when there is one. */
function TaskNote({ line, original }: { line: string; original: ReactNode }) {
  const body = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, color: zinc[200] }}>
      <StickyNote2OutlinedIcon aria-label="Task note" sx={{ flex: "none", fontSize: 14, color: DISPATCH_TASK }} />
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {line}
      </Box>
    </Box>
  );
  if (!original) return body;
  return (
    <Tooltip title={original} placement="bottom-start" slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}>
      {body}
    </Tooltip>
  );
}

/**
 * A Message cell with the row's task chip ahead of its text; the text alone, unwrapped, when the row has no open task.
 *
 * While the task carries a note, the note stands in for the text, and `original`, the message in full, shows on hover.
 */
export function WithTaskChip({
  row,
  original,
  children,
}: {
  row: DispatchTableRow;
  /** The message in full for the note's hover; null when the row has no message. */
  original: ReactNode;
  children: ReactNode;
}) {
  const state = useContext(DispatchTaskContext);
  const open = state ? rowTask(row, state.fold) : null;
  if (!state || !open) return <>{children}</>;
  const line = taskNoteLine(open, state.memberNameFor);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <TaskChip row={row} />
      <Box sx={{ minWidth: 0, flex: 1 }}>{line ? <TaskNote line={line} original={original} /> : children}</Box>
    </Box>
  );
}
